import type { DifferentProps } from '@/components/claims/Different';
import type { HeroProps } from '@/components/claims/Hero';
import type { HowItWorksProps } from '@/components/claims/HowItWorks';
import type { StatsProps } from '@/components/claims/Stats';
import { sharedConfig } from '@/pageConfig/shared';
import approvalImg from '@/static/claims/reddish/lemonade-claims-approval-process.png';
import aiImg from '@/static/claims/reddish/lemonade-claims-chatbot-ai.png';
import chatImg from '@/static/claims/reddish/lemonade-claims-chatbot.png';
import buttonImg from '@/static/claims/reddish/lemonade-claims-lemonade-app.png';
import type { BasePageConfig } from '../../BasePageConfig';
import { config as enUsConfig } from '../us/en';

export interface Config extends BasePageConfig {
  hero: HeroProps;
  stats: StatsProps;
  howItWorks: HowItWorksProps;
  different: DifferentProps;
}

export const config: Config = {
  layout: { ...sharedConfig['de-DE'].layout },
  seo: {
    ...enUsConfig.seo,
    title: 'Wie Versicherungsansprüche bei Lemonade funktionieren | Lemonade',
    metaTitle: 'Wie Versicherungsansprüche bei Lemonade funktionieren | Lemonade',
    description:
      'Lemonade bearbeitet Schadensfälle schnell und unkompliziert, dank unserer leistungsstarken Technologie und unserem engagierten Schadenteam. Reiche einen Schaden einfach über die Lemonade App ein und dein Schadenfall wird entweder sofort von einem Bot oder persönlich durch das Schadenregulierungs-Team von Lemonade bearbeitet.',
  },
  hero: {
    title: 'Das Geheimnis hinter der Schadenszahlung',
    subtitle:
      'Schadensfälle werden von unserer AI und unserem engagierten Team bearbeitet. Melde einen Schaden mühelos und schnell mit der Lemonade App.',
  },
  stats: {
    steps: [
      {
        ...enUsConfig.stats.steps[0],
        title: '3 Sek',
        subtitle: 'Unsere schnellste aufgezeichnete Schadensabwicklung',
      },
      {
        ...enUsConfig.stats.steps[1],
        subtitle: 'Positive Bewertungen unserer App',
      },
      {
        ...enUsConfig.stats.steps[2],
        subtitle: 'der Schadensfälle werden sofort bearbeitet',
      },
    ],
  },
  howItWorks: {
    title: 'Wie Lemonades Schadenmeldungen funktionieren',
    subtitle: 'Mühelose Schadenmeldung über dein Smartphone – überall, jederzeit',
    darkBackground: true,
    steps: {
      ...enUsConfig.howItWorks.steps,
      claim_button: {
        ...enUsConfig.howItWorks.steps.claim_button,
        image: buttonImg,
        content: `Tippe auf die "<b>Schaden</b>"-Taste in der Lemonade App`,
      },
      what_happened: {
        ...enUsConfig.howItWorks.steps.what_happened,
        image: chatImg,
        content: 'Sag uns, was passiert ist',
      },
      anti_fraud: {
        ...enUsConfig.howItWorks.steps.anti_fraud,
        image: aiImg,
        content: 'Unser AI führt Dutzende Algorithmen zur Betrugsbekämpfung durch',
      },
      instantly_approved: {
        ...enUsConfig.howItWorks.steps.instantly_approved,
        image: approvalImg,
        content: 'Falls dein Schaden <b>sofort</b> genehmigt wurde, bezahlt dich unsere AI in wenigen Sekunden',
      },
      humans: {
        ...enUsConfig.howItWorks.steps.humans,
        image: approvalImg,
        content:
          'Andernfalls übergibt unsere AI deine Schadenmeldung an unser engagiertes Team von <b>Zweibeinern</b>, die sie umgehend bearbeitet',
      },
    },
  },
  different: {
    title: 'Schadensfälle bezahlen – Gutes tun',
    subtitle: `Im Gegensatz zu anderen Versicherern, nehmen wir nur einen fixen Anteil von deinem Beitrag und verwenden den Rest, um deine Schadensfälle zu begleichen. Einmal im Jahr <a href="/de/giveback">spenden</a> wir dein nicht in Anspruch genommenes Geld an einen wohltätigen Zweck deiner Wahl. Wir verdienen nichts daran, wenn wir Ansprüche verzögern oder ablehnen, und bearbeiten sie deshalb <a href="/de/faq#claims">schnell und fair</a>.`,
    cta: {
      label: 'Check unsere preise',
    },
  },
};
